import { get, memoize, cloneDeep, set, some, find } from 'lodash';

import Log from 'utils/Log';

class TopNavigationSelector {
  // Get normalized node map for O(1) access to tree nodes
  static _getnodesById = state => get(state, ['topNavigation', 'nodesById']);

  // Get raw tree data without isActive flags
  static _getNodeTree = state => get(state, ['topNavigation', 'nodeTree']);

  static _getCurrentNodeId = state => get(state, ['topNavigation', 'currentNodeId']);

  static getCurrentNode = state =>
    get(TopNavigationSelector._getnodesById(state), [
      TopNavigationSelector._getCurrentNodeId(state),
    ]);

  // Parse navigation tree and set `isActive` flag on nodes based on the current node
  static _getCurrentTopNavigationTree = state => {
    Log.debug('uncached _getCurrentTopNavigationTree');
    // Clone tree to avoid mutating state
    const tree = cloneDeep(TopNavigationSelector._getNodeTree(state));
    const currentNodeId = TopNavigationSelector._getCurrentNodeId(state);

    // Recursively navigate node tree, marking nodes as active if:
    //   - they are the current node
    //   - they have a child node that is the current node
    const setActiveFlagRecursively = node => {
      // If tree node is current node, stop recursion by returning true
      if (get(node, 'id') === currentNodeId) {
        set(node, 'isActive', true);
        return true;
      }

      // Recursively traverse child nodes, and set this node as active if one its children is the current node
      const isActive = some(get(node, 'subNavigations'), setActiveFlagRecursively);
      if (isActive) {
        set(node, 'isActive', isActive);
      }

      return isActive;
    };

    setActiveFlagRecursively(tree);

    return tree;
  };

  // Memoize _getCurrentTopNavigationTree, using the current node id as the cache key
  static getCurrentTopNavigationTree = memoize(
    TopNavigationSelector._getCurrentTopNavigationTree,
    state =>
      JSON.stringify({
        currentNodeId: TopNavigationSelector._getCurrentNodeId(state),
      })
  );

  // Return active node at the specified depth, if any
  static _getActiveNodeAtDepth = (state, depth = 0) => {
    Log.debug('uncached _getActiveNodeAtDepth');
    const tree = TopNavigationSelector.getCurrentTopNavigationTree(state);

    const recursivelyFindActiveChild = (node, currentDepth) => {
      if (get(node, 'isActive')) {
        if (currentDepth === depth) {
          return node;
        }

        const activeChild = find(get(node, 'subNavigations'), { isActive: true });

        return recursivelyFindActiveChild(activeChild, currentDepth + 1);
      }

      return null;
    };

    return recursivelyFindActiveChild(tree, 0);
  };

  // Memoized _getActiveNodeAtDepth, using the current node id and the depth as the cache key
  static getActiveNodeAtDepth = memoize(
    TopNavigationSelector._getActiveNodeAtDepth,
    (state, depth) =>
      JSON.stringify({
        id: TopNavigationSelector._getCurrentNodeId(state),
        depth,
      })
  );
}

export default TopNavigationSelector;
